import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import {
  ArrowLeft,
  Users,
  Check,
  X,
  Award,
  MapPin,
  Briefcase,
  Sparkles,
  ChevronDown,
  ChevronUp,
  User,
  Search,
  Filter,
  Building2
} from "lucide-react";

const CandidateComparison = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get("job_id");
  
  const [candidates, setCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [selectedCandidates, setSelectedCandidates] = useState([]);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comparing, setComparing] = useState(false);
  const [job, setJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    location: "",
    industry: "",
    min_experience: "",
    skills: "",
    job_id: jobId || ""
  });

  useEffect(() => {
    fetchJobs();
    if (jobId) {
      fetchJobAndCandidates();
    } else {
      fetchAllCandidates();
    }
  }, [jobId]);

  useEffect(() => {
    // Apply local filtering when search query or filters change
    applyLocalFilters();
  }, [searchQuery, filters, candidates]);

  const fetchJobs = async () => {
    try {
      const response = await fetch(`${API}/jobs/recruiter`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setJobs(data || []);
      }
    } catch (error) {
      console.error("Failed to fetch jobs:", error);
    }
  };

  const applyLocalFilters = () => {
    let filtered = [...candidates];

    // Search by name
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(c => 
        (c.name || "").toLowerCase().includes(query) ||
        (c.headline || "").toLowerCase().includes(query)
      );
    }

    // Filter by location
    if (filters.location.trim()) {
      const loc = filters.location.toLowerCase();
      filtered = filtered.filter(c => 
        (c.location || "").toLowerCase().includes(loc)
      );
    }

    // Filter by industry
    if (filters.industry.trim()) {
      const ind = filters.industry.toLowerCase();
      filtered = filtered.filter(c => 
        (c.industries || []).some(i => i.toLowerCase().includes(ind))
      );
    }

    // Filter by minimum experience
    if (filters.min_experience) {
      const minExp = parseInt(filters.min_experience);
      filtered = filtered.filter(c => 
        (c.experience?.length || 0) >= minExp
      );
    }

    // Filter by skills
    if (filters.skills.trim()) {
      const skillList = filters.skills.toLowerCase().split(",").map(s => s.trim());
      filtered = filtered.filter(c => {
        const candidateSkills = (c.skills || []).map(s => s.toLowerCase());
        return skillList.some(skill => 
          candidateSkills.some(cs => cs.includes(skill))
        );
      });
    }

    setFilteredCandidates(filtered);
  };

  const clearFilters = () => {
    setSearchQuery("");
    setFilters({ location: "", industry: "", min_experience: "", skills: "", job_id: "" });
  };

  const handleJobFilterChange = (newJobId) => {
    setFilters(prev => ({ ...prev, job_id: newJobId }));
    if (newJobId) {
      navigate(`/candidates/compare?job_id=${newJobId}`);
    } else {
      navigate("/candidates/compare");
    }
  };

  const fetchJobAndCandidates = async () => {
    try {
      // Fetch job details
      const jobResponse = await fetch(`${API}/jobs/${jobId}`, {
        credentials: "include",
      });
      if (jobResponse.ok) {
        const jobData = await jobResponse.json();
        setJob(jobData);
      }

      // Fetch matched candidates for this job
      const response = await fetch(`${API}/matching/candidates/${jobId}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCandidates(data);
        setFilteredCandidates(data);
      }
    } catch (error) {
      console.error("Failed to fetch:", error);
      toast.error("Failed to load candidates");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllCandidates = async () => {
    try {
      const response = await fetch(`${API}/candidates/search`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        setCandidates(data);
        setFilteredCandidates(data);
      }
    } catch (error) {
      console.error("Failed to fetch:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCandidate = (candidateId) => {
    setSelectedCandidates(prev => {
      if (prev.includes(candidateId)) {
        return prev.filter(id => id !== candidateId);
      }
      if (prev.length >= 5) {
        toast.error("Maximum 5 candidates can be compared");
        return prev;
      }
      return [...prev, candidateId];
    });
  };

  const handleCompare = async () => {
    if (selectedCandidates.length < 2) {
      toast.error("Select at least 2 candidates to compare");
      return;
    }

    setComparing(true);
    try {
      const response = await fetch(`${API}/candidates/compare`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          candidate_ids: selectedCandidates,
          job_id: jobId
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setComparisonResult(data);
        setShowAnalysis(true);
      } else {
        toast.error("Failed to compare candidates");
      }
    } catch (error) {
      toast.error("Failed to compare candidates");
    } finally {
      setComparing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-obsidian flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-crimson border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />

      <main className="lg:ml-64 p-4 sm:p-6 lg:p-8 pt-20 lg:pt-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading text-2xl font-bold">COMPARE CANDIDATES</h1>
            {job && (
              <p className="text-gray-400 mt-1">
                For: {job.title} • {job.location}
              </p>
            )}
          </div>
          <Button
            onClick={handleCompare}
            disabled={selectedCandidates.length < 2 || comparing}
            className="bg-crimson hover:bg-crimson-hover text-white"
            data-testid="compare-btn"
          >
            {comparing ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Compare ({selectedCandidates.length})
              </>
            )}
          </Button>
        </div>

        {/* AI Analysis Results */}
        <AnimatePresence>
          {comparisonResult && showAnalysis && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-obsidian-light border border-crimson/30 p-6 mb-8"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-crimson" />
                  AI COMPARISON ANALYSIS
                </h3>
                <Button
                  onClick={() => setShowAnalysis(false)}
                  variant="ghost"
                  size="sm"
                  className="text-gray-400"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              <pre className="whitespace-pre-wrap font-body text-sm text-gray-300 leading-relaxed">
                {comparisonResult.ai_analysis}
              </pre>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search and Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search by name or headline..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-obsidian-light border-[#333] text-white"
                data-testid="search-candidates"
              />
            </div>
            <Button
              onClick={() => setShowFilters(!showFilters)}
              variant="outline"
              className={`border-[#333] text-white ${showFilters ? "border-crimson" : ""}`}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filters
            </Button>
          </div>

          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              className="bg-obsidian-light border border-[#222] p-4"
            >
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Job Role</label>
                  <select
                    value={filters.job_id}
                    onChange={(e) => handleJobFilterChange(e.target.value)}
                    className="w-full px-3 py-2 bg-obsidian border border-[#333] text-white rounded-md"
                    data-testid="filter-job-role"
                  >
                    <option value="">All Jobs</option>
                    {jobs.map(j => (
                      <option key={j.job_id} value={j.job_id}>{j.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Location</label>
                  <Input
                    placeholder="e.g., New York"
                    value={filters.location}
                    onChange={(e) => setFilters(prev => ({ ...prev, location: e.target.value }))}
                    className="bg-obsidian border-[#333] text-white"
                    data-testid="filter-location"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Industry</label>
                  <Input
                    placeholder="e.g., Technology"
                    value={filters.industry}
                    onChange={(e) => setFilters(prev => ({ ...prev, industry: e.target.value }))}
                    className="bg-obsidian border-[#333] text-white"
                    data-testid="filter-industry"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Min Experience (years)</label>
                  <Input
                    type="number"
                    placeholder="e.g., 3"
                    value={filters.min_experience}
                    onChange={(e) => setFilters(prev => ({ ...prev, min_experience: e.target.value }))}
                    className="bg-obsidian border-[#333] text-white"
                    data-testid="filter-experience"
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Skills</label>
                  <Input
                    placeholder="e.g., React, Python"
                    value={filters.skills}
                    onChange={(e) => setFilters(prev => ({ ...prev, skills: e.target.value }))}
                    className="bg-obsidian border-[#333] text-white"
                    data-testid="filter-skills"
                  />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={clearFilters} variant="outline" size="sm" className="border-[#333] text-white" data-testid="clear-filters-btn">
                  Clear All
                </Button>
              </div>
            </motion.div>
          )}

          {/* Results count */}
          <p className="text-sm text-gray-500">
            Showing {filteredCandidates.length} of {candidates.length} candidates
            {selectedCandidates.length > 0 && ` • ${selectedCandidates.length} selected`}
          </p>
        </div>

        {/* Candidate Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCandidates.map((candidate) => {
            const isSelected = selectedCandidates.includes(candidate.user_id);
            return (
              <motion.div
                key={candidate.user_id}
                onClick={() => toggleCandidate(candidate.user_id)}
                className={`bg-obsidian-light border p-6 cursor-pointer transition-all ${
                  isSelected 
                    ? "border-crimson bg-crimson/5" 
                    : "border-[#222] hover:border-[#444]"
                }`}
                whileHover={{ scale: 1.02 }}
                data-testid={`candidate-${candidate.user_id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-crimson/20 flex items-center justify-center flex-shrink-0">
                      {candidate.picture ? (
                        <img src={candidate.picture} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <User className="w-6 h-6 text-crimson" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-white">{candidate.name}</h3>
                      <p className="text-sm text-gray-400">{candidate.headline || "Professional"}</p>
                    </div>
                  </div>
                  <div className={`w-6 h-6 border-2 flex items-center justify-center ${
                    isSelected ? "border-crimson bg-crimson" : "border-[#444]"
                  }`}>
                    {isSelected && <Check className="w-4 h-4 text-white" />}
                  </div>
                </div>

                {candidate.match_score !== undefined && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-400">Match Score</span>
                      <span className="text-crimson font-bold">{candidate.match_score}%</span>
                    </div>
                    <div className="h-2 bg-[#222]">
                      <div 
                        className="h-full bg-crimson"
                        style={{ width: `${candidate.match_score}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="space-y-2 text-sm">
                  {candidate.location && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <MapPin className="w-4 h-4" />
                      <span>{candidate.location}</span>
                    </div>
                  )}
                  {candidate.career_level && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Briefcase className="w-4 h-4" />
                      <span className="capitalize">{candidate.career_level} Level</span>
                    </div>
                  )}
                </div>

                {candidate.skills && candidate.skills.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-4">
                    {candidate.skills.slice(0, 5).map((skill, idx) => (
                      <span
                        key={idx}
                        className={`px-2 py-1 text-xs ${
                          candidate.matching_skills?.includes(skill.toLowerCase())
                            ? "bg-crimson/20 text-crimson border border-crimson/30"
                            : "bg-[#1a1a1a] text-gray-400"
                        }`}
                      >
                        {skill}
                      </span>
                    ))}
                    {candidate.skills.length > 5 && (
                      <span className="px-2 py-1 text-xs bg-[#1a1a1a] text-gray-500">
                        +{candidate.skills.length - 5}
                      </span>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {filteredCandidates.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">
              {candidates.length === 0 ? "No candidates found" : "No candidates match your filters"}
            </p>
            {candidates.length > 0 && filteredCandidates.length === 0 && (
              <Button onClick={clearFilters} variant="outline" className="mt-4 border-[#333] text-white">
                Clear Filters
              </Button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default CandidateComparison;
